import React from 'react';
import classnames from 'classnames';
import { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Dropdown } from '@/components/shared/Dropdown';
import { AdminRole, adminRoles } from '@/types/affiliation';
import { Label } from '@/components/shared/Label';
import { Input } from '@/components/shared/Input';
import { SharedButton } from '@/components/shared/Button';

import * as styles from './Style.module.scss';

export type Values = {
  email: string;
  role: AdminRole;
};

type Props = {
  errors: Partial<Record<keyof Values, string>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<Values>>;
  isLoading: boolean;
  values: Values;
};
export const FormContents: React.FC<Props> = ({
  errors,
  handleChange,
  handleSubmit,
  setFieldValue,
  isLoading,
  values,
}) => {
  const { t } = useTranslation();
  const handleChangeRole = (selectedRole: AdminRole) =>
    setFieldValue('role', selectedRole);
  return (
    <Styled>
      <form onSubmit={handleSubmit}>
        <Field>
          <FieldLabel>{t('models.contact.email')}</FieldLabel>
          <Input
            data-test="email"
            className={classnames({ 'is-error': errors.email })}
            name="email"
            onChange={handleChange}
            value={values.email}
          />
          {errors.email && <Label>{errors.email}</Label>}
        </Field>
        <Field>
          <FieldLabel>{t('models.affiliation.role')}</FieldLabel>
          <Dropdown
            dataTest="roles"
            text={t(`models.affiliation.roleType.${values.role}.name`)}
            buttonClassNames={styles.toggleButton}
            menuWrapperClassNames={styles.menuWrapper}
            menuNode={adminRoles.map((roleItem) => (
              <div
                key={roleItem}
                data-test={`${roleItem}-role`}
                onClick={() => handleChangeRole(roleItem)}
                className={classnames(
                  styles.menuItem,
                  values.role === roleItem ? 'active' : '',
                )}
              >
                {t(`models.affiliation.roleType.${roleItem}.name`)}

                <div className={styles.menuItemDescription}>
                  {t(`models.affiliation.roleType.${roleItem}.description`)}
                </div>
              </div>
            ))}
          />
        </Field>
        <ButtonGroup>
          <SharedButton dataTest="do-invite" type="submit" disabled={isLoading}>
            {t('ui.action.inviteNow')}
          </SharedButton>
        </ButtonGroup>
      </form>
    </Styled>
  );
};

const Styled = styled.div`
  ${Input} {
    width: 100%;
  }
`;

const Field = styled.div`
  margin: 0 0 1em;
`;

const FieldLabel = styled.div`
  display: block;
  line-height: 1;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
`;

const ButtonGroup = styled.div`
  margin: 3.5em 0 7.5em;
  text-align: center;
`;
